import { useState, useMemo, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Tabs from "aldoo-ra/Tabs"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import { ArrowUpTrayIcon, XMarkIcon, EnvelopeIcon, MagnifyingGlassIcon, DocumentTextIcon } from "@heroicons/react/24/outline"
import { addContactsBulk } from "./controller"

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi

// Parse free-form text or CSV content into a deduped list of email-like
// tokens. Accepts one-per-line, comma-separated, or semicolon-separated
// inputs — anything matching the standard email shape is kept. The CSV
// case is identical at this stage because we only care about email
// columns; if a richer header-aware CSV import is needed later, swap
// this for a real parser.
function extractEmails(text) {
    if (!text) return []
    const matches = text.match(EMAIL_RE) || []
    return [...new Set(matches.map((e) => e.trim().toLowerCase()))]
}

export default function ImportContactsDialog({ existingEmails, onImported, onClose, texts }) {
    const [raw, setRaw] = useState("")
    const [importing, setImporting] = useState(false)
    const [result, setResult] = useState(null) // { added, skipped, totalAdded }
    const [source, setSource] = useState("manual")
    const [fileName, setFileName] = useState("")
    const [search, setSearch] = useState("")
    const fileRef = useRef(null)

    const parsed = useMemo(() => extractEmails(raw), [raw])
    const existing = useMemo(
        () => new Set((existingEmails || []).map((e) => (e || "").toLowerCase())),
        [existingEmails],
    )

    const { newEmails, dupEmails } = useMemo(() => {
        const ne = []
        const de = []
        for (const e of parsed) {
            if (existing.has(e)) de.push(e)
            else ne.push(e)
        }
        return { newEmails: ne, dupEmails: de }
    }, [parsed, existing])

    const handleFile = (file) => {
        if (!file) return
        setFileName(file.name)
        const reader = new FileReader()
        reader.onload = () => {
            setRaw(String(reader.result || ""))
        }
        reader.readAsText(file)
    }

    const handleRemove = (email) => {
        const r = new RegExp(email.replace(/[.+*?^$()|[\]{}\\-]/g, "\\$&"), "gi")
        setRaw((prev) => prev.replace(r, "").replace(/[\r\n,;\s]+/g, "\n").trim())
    }

    const handleImport = async () => {
        if (!newEmails.length) return
        setImporting(true)
        try {
            const res = await addContactsBulk(newEmails)
            if (res?.result) {
                showSuccessToast(
                    texts?.contactsImported,
                    `${res.totalAdded || 0} ${texts?.addedToContactsBulk}`,
                    { position: TOAST_POSITIONS.TOP_CENTER, pauseOnHover: false, enableDismissalTracking: false, duration: 2200 },
                )
                onImported?.(res.added || [])
                onClose?.()
            }
        } finally {
            setImporting(false)
        }
    }

    const footerContent = (
        <div className="flex items-center justify-between gap-3 w-full">
            <Typography variant="txt2_dim" className="leading-tight">
                {parsed.length === 0
                    ? texts?.importHint
                    : `${newEmails.length} ${texts?.toImport}${dupEmails.length ? ` · ${dupEmails.length} ${texts?.alreadyInContacts}` : ""}`}
            </Typography>
            <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose} disabled={importing}>
                    {texts?.cancel}
                </Button>
                <Button variant="primary" onClick={handleImport} disabled={importing || !newEmails.length}>
                    {importing ? texts?.importing : texts?.import}
                </Button>
            </div>
        </div>
    )

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={texts?.importContacts}
            variant="transfer_medium"
            closeOnClickOutside={!importing}
            closeOnEscape={!importing}
            footerContent={footerContent}
        >
            <div className="p-5 flex flex-col gap-4">
                <Tabs
                    tabs={[
                        { value: "manual", label: texts?.manualTab },
                        { value: "csv", label: texts?.csvTab },
                    ]}
                    value={source}
                    onChange={setSource}
                />

                {source === "manual" ? (
                    <>
                        <Typography variant="txt2_dim">
                            {texts?.manualDesc}
                        </Typography>
                        <textarea
                            value={raw}
                            onChange={(e) => setRaw(e.target.value)}
                            rows={6}
                            placeholder={texts?.emailsPlaceholder}
                            className="w-full rounded-xl border border-bordered bg-paper px-3 py-2.5 text-sm text-text placeholder-text_secondary focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none font-mono"
                            disabled={importing}
                            autoFocus
                        />
                    </>
                ) : (
                    <div className="flex flex-col gap-3">
                        <Typography variant="txt2_dim">
                            {texts?.csvDesc}
                        </Typography>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="secondary_s"
                                onClick={() => fileRef.current?.click()}
                                disabled={importing}
                            >
                                <ArrowUpTrayIcon className="w-4 h-4" strokeWidth={2} />
                                {fileName ? texts?.replaceFile : texts?.chooseFile}
                            </Button>
                            {fileName && (
                                <div className="flex items-center gap-1.5 text-sm text-text_secondary min-w-0">
                                    <DocumentTextIcon className="w-4 h-4 shrink-0" />
                                    <span className="truncate">{fileName}</span>
                                </div>
                            )}
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".csv,text/csv,text/plain"
                                className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = "" }}
                            />
                        </div>
                    </div>
                )}

                {parsed.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-3">
                            <Typography variant="txt1_accent" className="text-text">
                                {texts?.parsedList}{" "}
                                <span className="text-text_secondary font-normal">({parsed.length})</span>
                            </Typography>
                            <div className="relative w-[220px]">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text_secondary pointer-events-none" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={texts?.searchEmails}
                                    className="w-full rounded-xl border border-bordered bg-paper pl-9 pr-3 py-2 text-sm text-text placeholder-text_secondary focus:outline-none focus:ring-1 focus:ring-primary/50"
                                />
                            </div>
                        </div>
                        <div className="bg-canvas rounded-xl border border-bordered overflow-hidden max-h-[260px] overflow-y-auto">
                            {(() => {
                                const q = search.trim().toLowerCase()
                                const list = q ? parsed.filter((e) => e.includes(q)) : parsed
                                if (!list.length) {
                                    return (
                                        <div className="px-4 py-6 text-center">
                                            <Typography variant="txt2_dim">{texts?.noMatchingEmails}</Typography>
                                        </div>
                                    )
                                }
                                return list.map((email) => {
                                    const dup = existing.has(email)
                                    return (
                                        <div
                                            key={email}
                                            className="flex items-center gap-3 px-4 py-2 border-b border-bordered last:border-b-0"
                                        >
                                            <EnvelopeIcon className={`w-4 h-4 shrink-0 ${dup ? "text-text_secondary/60" : "text-primary"}`} />
                                            <span className={`flex-1 min-w-0 text-sm truncate ${dup ? "text-text_secondary line-through" : "text-text"}`}>
                                                {email}
                                            </span>
                                            {dup && (
                                                <span className="text-xs text-text_secondary shrink-0">
                                                    {texts?.duplicate}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleRemove(email)}
                                                disabled={importing}
                                                className="p-1 rounded text-text_secondary hover:text-error hover:bg-neutral_1 transition-colors disabled:opacity-40 shrink-0"
                                                title={texts?.remove}
                                            >
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )
                                })
                            })()}
                        </div>
                    </div>
                )}

                {result && (
                    <Typography variant="txt2_dim">
                        {`${result.totalAdded} ${texts?.contactsAddedSuffix}.`}
                    </Typography>
                )}
            </div>
        </Modal>
    )
}
